import React from "react";
import ComparisonPageTemplate from "../../components/ComparisonPageTemplate/ComparisonPageTemplate";

const CANONICAL_URL = "https://teamgrid.ai/compare/teamgrid-ai-vs-activtrak";

const FAQ_ITEMS = [
  {
    question: "Does my organization get suspended immediately once the subscription expires?",
    answer: "Yes. If the subscription is not renewed by the due date, the TeamGrid account is immediately locked, and access to the platform is paused. All data remains secure and intact, and access is fully restored once the subscription is renewed.",
  },
  {
    question: "Can monitoring software see personal activities?",
    answer: "No. TeamGrid does not track any personal data. It is designed to focus only on work-related activity during active tracking sessions. Personal time, breaks, and non-work activity are never monitored. TeamGrid respects work-life boundaries and ensures employee privacy is always maintained.",
  },
  {
    question: "Are there any privacy concerns?",
    answer: "TeamGrid is built with strict privacy principles. It only collects work-related data during active tracking sessions and never monitors personal activities, including during breaks or outside work hours. Users remain in control, and all data is handled securely as outlined in our Privacy Policy.",
  },
  {
    question: "How do I book a TeamGrid demo?",
    answer: "You can easily book a live demo session through our website. Just follow these steps: Go to Book a Demo → Select a date → Choose a time slot → Fill in your details → Confirm the booking. A member of our team will reach out to guide you through the platform.",
  },
  {
    question: "Is TeamGrid suitable for remote and hybrid teams?",
    answer: "Absolutely. TeamGrid is built for distributed teams with privacy-first principles.",
  },
];

const JSON_LD_FAQ = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${CANONICAL_URL}#faq`,
  mainEntity: [
    {
      "@type": "Question",
      name: "Does my organization get suspended immediately once the subscription expires?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. If the subscription is not renewed by the due date, the TeamGrid account is immediately locked and access is paused. All data remains secure and intact, and access is fully restored once the subscription is renewed.",
      },
    },
    {
      "@type": "Question",
      name: "Can monitoring software see personal activities?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "No. TeamGrid does not track personal activities. It focuses only on work-related activity during active tracking sessions. Personal time, breaks, and non-work activity are not monitored.",
      },
    },
    {
      "@type": "Question",
      name: "Are there any privacy concerns?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "TeamGrid is built with strict privacy principles. It collects work-related data only during active tracking sessions and does not monitor personal activities during breaks or outside work hours. Users remain in control, and data is handled securely as outlined in the Privacy Policy.",
      },
    },
    {
      "@type": "Question",
      name: "How do I book a TeamGrid demo?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "You can book a live demo through the website by going to Book a Demo, selecting a date and time slot, filling in your details, and confirming the booking. A team member will reach out to guide you through the platform.",
      },
    },
    {
      "@type": "Question",
      name: "Is TeamGrid suitable for remote and hybrid teams?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Absolutely. TeamGrid is built for distributed teams with privacy-first principles.",
      },
    },
  ],
};

const JSON_LD_GRAPH = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": "https://teamgrid.ai/#website",
      url: "https://teamgrid.ai/",
      name: "TeamGrid",
    },
    {
      "@type": "Organization",
      "@id": "https://teamgrid.ai/#organization",
      name: "TeamGrid",
      url: "https://teamgrid.ai/",
    },
    {
      "@type": "WebPage",
      "@id": `${CANONICAL_URL}#webpage`,
      url: CANONICAL_URL,
      name: "Switch from ActivTrak to TeamGrid: The Privacy-First ActivTrak Alternative",
      description: "TeamGrid is a modern, privacy-first ActivTrak alternative that helps leaders understand how work happens using AI-powered workforce intelligence—without activity scoring, digital exhaust tracking, or employee surveillance.",
      isPartOf: { "@id": "https://teamgrid.ai/#website" },
      about: [
        { "@id": "https://teamgrid.ai/#teamgrid-app" },
        { "@id": "https://www.activtrak.com/#activtrak-app" },
      ],
      breadcrumb: { "@id": `${CANONICAL_URL}#breadcrumbs` },
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${CANONICAL_URL}#breadcrumbs`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://teamgrid.ai/" },
        { "@type": "ListItem", position: 2, name: "Compare", item: "https://teamgrid.ai/compare/" },
        { "@type": "ListItem", position: 3, name: "TeamGrid vs ActivTrak", item: CANONICAL_URL },
      ],
    },
    {
      "@type": "SoftwareApplication",
      "@id": "https://teamgrid.ai/#teamgrid-app",
      name: "TeamGrid",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Windows, macOS",
      url: "https://teamgrid.ai/",
      publisher: { "@id": "https://teamgrid.ai/#organization" },
      description: "A privacy-first workforce intelligence platform that helps leaders understand how work happens using AI-powered organizational insights—without employee surveillance.",
      featureList: [
        "Workforce intelligence and decision support",
        "Conversational AI with predictive performance signals",
        "Ask TeamGrid (AI chat) with instant answers and downloadable summaries",
        "Real-time analytics with team-level trends and organizational insights",
        "Organizational Intelligence Engine detects friction points and performance gaps",
        "Auto-generated daily, hourly, and weekly work summaries",
        "One-click, executive-ready reports",
        "Privacy-first design without activity scoring or surveillance",
      ],
    },
    {
      "@type": "SoftwareApplication",
      "@id": "https://www.activtrak.com/#activtrak-app",
      name: "ActivTrak",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Windows, macOS, Web",
      url: "https://www.activtrak.com/",
      publisher: { "@type": "Organization", name: "ActivTrak", url: "https://www.activtrak.com/" },
      description: "A workforce analytics and monitoring platform focused on activity dashboards, behavior metrics, and productivity scoring.",
      featureList: [
        "Behavior monitoring and activity scoring",
        "Application and website usage tracking",
        "Activity-focused dashboards",
        "CSV-driven exports and reports",
      ],
    },
    {
      "@type": "ItemList",
      "@id": `${CANONICAL_URL}#comparison`,
      name: "TeamGrid vs ActivTrak – Feature Comparison",
      description: "Side-by-side comparison of TeamGrid and ActivTrak across philosophy, AI insights, privacy approach, analytics depth, and decision intelligence.",
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      numberOfItems: 2,
      itemListElement: [
        { "@type": "ListItem", position: 1, item: { "@id": "https://teamgrid.ai/#teamgrid-app" } },
        { "@type": "ListItem", position: 2, item: { "@id": "https://www.activtrak.com/#activtrak-app" } },
      ],
    },
  ],
};

const COMPARISON_TABLE_ROWS = [
  { feature: "Core Philosophy", teamgrid: "Workforce intelligence & decision support", competitor: "Behavior monitoring & activity scoring" },
  { feature: "AI-Powered Insights", teamgrid: "Conversational AI with predictive performance signals", competitor: "Limited AI; rule-based insights" },
  { feature: "Ask TeamGrid (AI Chat)", teamgrid: "Instant answers and downloadable summaries", competitor: "Not available" },
  { feature: "Real-Time Analytics", teamgrid: "Team-level trends and organizational insights", competitor: "Activity-focused dashboards" },
  { feature: "Organizational Intelligence", teamgrid: "Automatically detects performance gaps and friction points", competitor: "Focused on individual behavior metrics" },
  { feature: "Work Summaries", teamgrid: "Auto-generated daily, hourly, and weekly insights", competitor: "Requires manual interpretation" },
  { feature: "Downloadable Reports", teamgrid: "One-click, executive-ready summaries", competitor: "CSV-heavy exports with limited visualization" },
  { feature: "Built for Every Team", teamgrid: "Yes designed for all team structures", competitor: "Monitoring-first approach limits flexibility" },
];

const DATA_DECISIONS_TEAMGRID_BULLETS = [
  "Built as a modern ActivTrak alternative focused on understanding work, not scoring behavior",
  "Delivers actionable performance insights without labeling or ranking individuals",
  "Ask TeamGrid enables leaders to instantly explore trends, risks, and workload patterns using AI",
  "Prioritizes transparency and clarity over constant evaluation",
  "Clean, intuitive interface teams feel comfortable using every day",
  "Simple onboarding: lightweight desktop app for data capture and a web dashboard for real-time insights",
];

const DATA_DECISIONS_ACTIVTRAK_BULLETS = [
  "Focuses on tracking application usage, websites accessed, and activity signals",
  "Relies on productivity scores and benchmarks derived from behavior patterns",
  "Interpreted by many teams as a monitoring-first approach, which can impact trust and adoption",
  "Requires managers to interpret raw activity data to understand performance",
];

const DATA_DECISIONS2_TEAMGRID_BULLETS = [
  "Built as a best alternative to ActivTrak for productivity tracking, focused on outcomes, not activity volume",
  "Organizational Intelligence Engine highlights friction points, workload imbalance, and inefficiencies",
  "Daily and weekly summaries clearly explain what's changing and why performance is shifting",
  "AI recommends timely, actionable steps to improve team outcomes",
  "Helps leaders move from passive monitoring to proactive, confident management",
  "Designed to convert raw data into clear, strategic decisions teams can act on immediately",
];

const DATA_DECISIONS2_ACTIVTRAK_BULLETS = [
  "Tracks application and website usage, which doesn't always represent real productivity or impact",
  "Managers often spend significant time manually interpreting charts and activity dashboards",
  "Insights tend to be reactive, surfacing only after performance issues become visible",
  "Focus remains on observation rather than decision support",
];

const WHAT_YOU_GET_ITEMS = [
  "Ask TeamGrid – AI-powered, on-demand performance insights",
  "Organizational Intelligence – Detects risks and inefficiencies early",
  "Real-Time Team Health – Clear view of workload and engagement",
  "Smart Summaries – Daily, hourly, and weekly performance trends",
  "Privacy-First Design – No surveillance, just trusted data",
  "One-Click Reports – Easy to export, ready to share",
  "Built for Remote Teams – Ideal for hybrid and distributed setups",
  "Quick Onboarding – Fast setup with an intuitive interface",
];

const KEY_FEATURE_CARDS = [
  {
    title: "Ask TeamGrid – Your AI Workforce Assistant",
    intro: "Interact with your organizational data through an AI-powered assistant that delivers instant, leadership-ready insights.",
    bullets: [],
    closing: "",
  },
  {
    title: "Privacy-First by Design",
    intro: "TeamGrid is built on transparency. It delivers aggregated performance intelligence without intrusive monitoring.",
    bullets: [],
    closing: "",
  },
  {
    title: "Organizational Intelligence",
    intro: "Automatically surfaces early signals of burnout risk, workload imbalances, and team-level inefficiencies so you can act before issues grow.",
    bullets: [],
    closing: "",
  },
  {
    title: "Decision-Ready Summaries",
    intro: "Get executive-ready insights and one-click reports so you can act on performance trends without manual analysis.",
    bullets: [],
    closing: "",
  },
];

const ActivTrakComparison = () => (
  <ComparisonPageTemplate
    seoTitle="ActivTrak Alternative | Privacy-First AI Insights with TeamGrid"
    seoDescription="Switch from ActivTrak to TeamGrid. Get AI-powered workforce intelligence without behavior tracking or surveillance. Built for trust and clarity."
    seoCanonical={CANONICAL_URL}
    jsonLdGraph={JSON_LD_GRAPH}
    jsonLdFaq={JSON_LD_FAQ}
    heroBadge="Comparison Guide"
    heroTitleLine1="Switch from ActivTrak to TeamGrid"
    heroTitleLine2={null}
    heroTitleLine3={null}
    heroTitleLine4={null}
    heroDescription="TeamGrid is a modern, privacy-first ActivTrak alternative that helps leaders understand how work happens using AI-powered workforce intelligence without activity scoring, digital exhaust tracking, or employee surveillance."
    competitorName="ActivTrak"
    competitorLogoUrl="/ActivTrek_logo_ladstpage.webp"
    comparisonTableRows={COMPARISON_TABLE_ROWS}
    keyFeaturesTitle="Why Teams Choose TeamGrid"
    keyFeaturesSubtitle="Ask TeamGrid – Your AI Workforce Assistant"
    keyFeatureCards={KEY_FEATURE_CARDS}
    whatYouGetItems={WHAT_YOU_GET_ITEMS}
    dataDecisionsHeading="Why Teams Prefer Clear Insights Over Surveillance Metrics"
    dataDecisionsSubtitle="As teams scale, leaders need clear visibility into how work is progressing without creating friction, resistance, or cultural strain. Traditional monitoring tools can provide data, but long-term adoption often depends on how that visibility is delivered and perceived by teams."
    dataDecisionsTeamGridBullets={DATA_DECISIONS_TEAMGRID_BULLETS}
    dataDecisionsCompetitorBullets={DATA_DECISIONS_ACTIVTRAK_BULLETS}
    dataDecisionsCompetitorCardTitle="ActivTrak"
    dataDecisions2Heading="Move from Activity Dashboards to Decision Intelligence"
    dataDecisions2Subtitle="Data only creates value when it leads to clear decisions and action. That's why many teams look beyond dashboards and search for the best ActivTrak alternative for productivity tracking and employee monitoring."
    dataDecisions2TeamGridBullets={DATA_DECISIONS2_TEAMGRID_BULLETS}
    dataDecisions2CompetitorBullets={DATA_DECISIONS2_ACTIVTRAK_BULLETS}
    dataDecisions2CompetitorCardTitle="ActivTrak"
    outcomesTitle="Performance Outcomes That Matter"
    outcomesImageSrc="/bottom_activtrek_section.webp"
    outcomesImageAlt="Clarity That Drives Results - Up to 45% faster decision making through real-time insights, Eliminates reliance on employee monitoring for productivity visibility, Delivers insights 5x faster than manual data analysis methods"
    trustedBadges={[
      "Privacy-first time tracking",
      "Ask TeamGrid AI assistant",
      "Real-time analytics & insights",
      "Free for up to 5 users",
    ]}
    faqs={FAQ_ITEMS}
    faqTitle="Frequently Asked Questions"
    faqSubtitle="Common questions about TeamGrid vs. ActivTrak."
  />
);

export default ActivTrakComparison;
